import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PaymentReturn.css';

const REDIRECT_SECONDS = 5;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const PaymentReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [balance, setBalance] = useState(null);
  const [amountPaid, setAmountPaid] = useState(null);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'

  const countdownRef = useRef(null);
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);

  const redirectStatus = searchParams.get('redirect_status');
  const paymentIntentSecret = searchParams.get('payment_intent_client_secret');

  const fetchBalance = async () => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');

    const res = await fetch(
      `${process.env.REACT_APP_FQDN}/api/check-group-credits`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      }
    );

    if (!res.ok) throw new Error('Failed to fetch balance');

    const data = await res.json();
    return data.group_credits;
  };

  const startCountdown = () => {
    if (countdownRef.current) return;

    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          navigate('/profile-management');
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const resolveSuccess = (newCredits) => {
    clearInterval(pollRef.current);
    clearTimeout(timeoutRef.current);

    setBalance((newCredits / 100).toFixed(2));
    setStatus('success');

    localStorage.removeItem('balanceBeforePayment');
  };

  useEffect(() => {
    if (redirectStatus !== 'succeeded') {
      setStatus('error');
      return;
    }

    // Fetch amount paid (non-critical)
    if (paymentIntentSecret) {
      const paymentIntentId = paymentIntentSecret.split('_secret_')[0];
      const token = localStorage.getItem('token');

      fetch(`${process.env.REACT_APP_FQDN}/api/payment-intent/${paymentIntentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.amount) {
            setAmountPaid((data.amount / 100).toFixed(2));
          }
        })
        .catch(() => { });
    }

    const balanceBefore = parseInt(
      localStorage.getItem('balanceBeforePayment') ?? '-1',
      10
    );

    pollRef.current = setInterval(async () => {
      try {
        const newCredits = await fetchBalance();
        if (newCredits > balanceBefore) {
          resolveSuccess(newCredits);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(async () => {
      clearInterval(pollRef.current);
      try {
        const newCredits = await fetchBalance();
        resolveSuccess(newCredits);
      } catch {
        setStatus('success');
        localStorage.removeItem('balanceBeforePayment');
      }
    }, POLL_TIMEOUT_MS);

    return () => {
      clearInterval(pollRef.current);
      clearTimeout(timeoutRef.current);
      clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔑 START COUNTDOWN ONLY AFTER BALANCE IS SET
  useEffect(() => {
    if (status === 'success' && balance !== null) {
      startCountdown();
    }
  }, [status, balance]);

  const handleGoNow = () => {
    clearInterval(pollRef.current);
    clearTimeout(timeoutRef.current);
    clearInterval(countdownRef.current);
    navigate('/profile-management');
  };

  if (status === 'loading') {
    return (
      <div className="pr-page">
        <div className="pr-card">
          <div className="pr-spinner-wrap">
            <div className="pr-spinner" />
          </div>
          <p className="pr-loading-text">Confirming your payment…</p>
          <p className="pr-loading-subtext">Waiting for your balance to update</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="pr-page">
        <div className="pr-card pr-card--error">
          <div className="pr-icon pr-icon--error">✕</div>
          <h1 className="pr-title">Payment Unsuccessful</h1>
          <p className="pr-subtitle">
            Something went wrong and your payment could not be completed. You have not been charged.
          </p>
          <button className="pr-btn" onClick={handleGoNow}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pr-page">
      <div className="pr-card">
        <div className="pr-icon-wrap">
          <svg className="pr-checkmark" viewBox="0 0 52 52">
            <circle className="pr-checkmark__circle" cx="26" cy="26" r="25" fill="none" />
            <path className="pr-checkmark__check" fill="none" d="M14 27l8 8 16-16" />
          </svg>
        </div>

        <h1 className="pr-title">Payment Successful</h1>
        <p className="pr-subtitle">Your balance has been topped up.</p>

        <div className="pr-details">
          {amountPaid && (
            <div className="pr-detail-row">
              <span className="pr-detail-label">Amount paid</span>
              <span className="pr-detail-value">£{amountPaid}</span>
            </div>
          )}
          {balance !== null && (
            <div className="pr-detail-row pr-detail-row--highlight">
              <span className="pr-detail-label">New balance</span>
              <span className="pr-detail-value pr-detail-value--balance">
                £{balance}
              </span>
            </div>
          )}
        </div>

        <button className="pr-btn" onClick={handleGoNow}>
          Go to Dashboard
        </button>

        <p className="pr-redirect-note">
          Redirecting in <span className="pr-countdown">{countdown}</span>s…
        </p>
      </div>
    </div>
  );
};

export default PaymentReturn;

